import { useCallback, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { useHistory } from 'react-router-dom';

import {
  StepAction,
  UseStepperConfig,
  UseStepperReturn,
  WizardStepContextValue,
} from './types';

/**
 * Thrown by a step's Next action when the user called the Next off, answering
 * Cancel to a question it asked: the wizard stays on the step, as for any Next
 * that does not go through, but does not mark the step failed. Any other error
 * marks it.
 */
class NextCancelled extends Error {}

/**
 * Encapsulates all stepper navigation logic — step tracking, async action
 * execution, loading/error state, and step-registered state management.
 *
 * @param {UseStepperConfig} config - Steps array and onCancel callback
 * @return {UseStepperReturn} Stepper state and handlers for FullPageWizard
 */
const useStepper = (config: UseStepperConfig): UseStepperReturn => {
  const initialIndex = config.steps.findIndex((s) => s.isInitialStep);
  const [currentStep, setCurrentStep] = useState(
    initialIndex >= 0 ? initialIndex : 0
  );
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(() => {
    if (initialIndex <= 0) return new Set<number>();
    return new Set(Array.from({ length: initialIndex }, (_, i) => i));
  });
  const [hasError, setHasError] = useState(false);
  const [loadingAction, setLoadingAction] = useState<StepAction | null>(null);

  // Step-registered state (from useWizardStep context)
  // null = unregistered (treated as enabled for free navigation)
  const [nextEnabled, _setNextEnabled] = useState<boolean | null>(null);
  const [backEnabled, _setBackEnabled] = useState<boolean | null>(null);
  const [onNext, _setOnNext] = useState<
    (() => void | Promise<void>) | undefined
  >();

  // Navigation guard — steps can disable it (e.g. after successful save)
  const [navigationGuardEnabled, setNavigationGuardEnabled] =
    useState<boolean>(true);

  const history = useHistory();

  /**
   * Clear the navigation guard and navigate away — the correct way to leave
   * the wizard after a terminal action (save / submit / compare).
   *
   * The guard-off state update is flushed synchronously via `flushSync`
   * BEFORE navigating, so the route-leave prompt reads the fresh `false`
   * value. A plain `setNavigationGuard(false)` immediately followed by an
   * imperative `history.push(...)` races: React 18 auto-batches the state
   * update (in every context, not just event handlers), so the guard-off
   * re-render has not committed by the time the synchronous push fires and
   * NavigationGuard's `when` predicate still sees the stale `true` — popping
   * the "unsaved changes" dialog on a successful save (INS-11398).
   *
   * @param {string} to - Destination path (react-router push) or absolute URL
   *   when `external` is set.
   * @param {{ external?: boolean }} [opts] - When `external` is true, hard
   *   navigate via `window.location.href` instead of a react-router push
   *   (e.g. leaving the SPA).
   */
  const finishWizard = useCallback(
    (to: string, opts?: { external?: boolean }) => {
      flushSync(() => setNavigationGuardEnabled(false));
      if (opts?.external) {
        window.location.href = to;
      } else {
        history.push(to);
      }
    },
    [history]
  );

  /**
   * Navigate to a specific step index, resetting all step-registered state.
   * Must be synchronous — React runs child effects before parent effects,
   * so the new step's registration would fire first, then a parent useEffect
   * reset would wipe it out.
   *
   * @param {number} index - Target step index
   */
  const setStep = (index: number) => {
    _setNextEnabled(null);
    _setBackEnabled(null);
    _setOnNext(undefined);
    setHasError(false);
    setCurrentStep(index);
  };

  // Context value for WizardStepContext.Provider — stable reference via useMemo
  const stepContextValue: WizardStepContextValue = useMemo(
    () => ({
      setNextEnabled: _setNextEnabled,
      setBackEnabled: _setBackEnabled,
      // Wrap handler in a function to avoid React treating it as a setState updater
      setOnNext: (handler: () => void | Promise<void>) =>
        _setOnNext(() => handler),
      setNavigationGuard: setNavigationGuardEnabled,
      finishWizard,
      goToStep: setStep,
    }),
    [finishWizard]
  );

  /**
   * Handle Next/Submit button click. Async — awaits the step-registered
   * onNext action, manages loading and error state, and advances on success.
   * On the last step, does not advance (consumer handles post-submission).
   */
  const handleNext = async () => {
    if (loadingAction) return;

    setLoadingAction('next');
    try {
      await onNext?.();
      setHasError(false);
      const isLast = currentStep === config.steps.length - 1;
      if (!isLast) {
        setCompletedSteps((prev) => new Set(prev).add(currentStep));
        setStep(currentStep + 1);
      }
    } catch (error) {
      setHasError(!(error instanceof NextCancelled));
    } finally {
      setLoadingAction(null);
    }
  };

  /**
   * Handle Back button click. Synchronous — navigates to the previous step.
   */
  const handleBack = () => {
    if (loadingAction) return;
    setStep(currentStep - 1);
  };

  /**
   * Handle Cancel button click. Synchronous — delegates to consumer callback.
   */
  const handleCancel = () => {
    if (loadingAction) return;
    config.onCancel();
  };

  /**
   * Handle progress indicator step click. Synchronous — navigates to a
   * completed step if backward navigation is allowed.
   *
   * @param {number} index - Target step index
   */
  const goToStep = (index: number) => {
    if (loadingAction) return;
    if (!completedSteps.has(index) || index === currentStep) return;
    if (backEnabled === false) return;
    setStep(index);
  };

  return {
    currentStep,
    completedSteps,
    hasError,
    loadingAction,
    isNextEnabled: nextEnabled !== false,
    isBackEnabled: backEnabled !== false && currentStep !== 0,
    handleNext,
    handleBack,
    handleCancel,
    goToStep,
    isFirstStep: currentStep === 0,
    isLastStep: currentStep === config.steps.length - 1,
    navigationGuardEnabled,
    stepContextValue,
  };
};

export { NextCancelled, useStepper };
